import { useState, useContext, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { UserContext } from '../../contexts/user-context';
import { Link } from 'react-router-dom';
import { FFHelpers, FFText, FFEmail, FFTelephone, RCFormProvider } from '@jlafica/rcform';
import { svc_createUser, svc_updateUser } from "../../services/user.service";
import { fetchRetryWithDelay } from '../../utilities/general-utils';

const ProfileForm = ({
    onFormSubmit
}) => {
    const {
        getDDUser,
        ddUser,
        userProfileCompleted,
        setUserProfileCompleted
    } = useContext(UserContext);

    const [formRef, setFormRef] = useState(null);
    const { getAccessTokenSilently, user } = useAuth0();
    const [appliedDDUserData, setAppliedDDUserData] = useState(false);

    const fieldNames = {
        username: "username",
        firstname: "firstname",
        lastname: "lastname",
        email: "email",
        phone: "phone",
    };

    const initialState = {
        [fieldNames.username]:  { ...FFHelpers.ffInitialFieldState.input },
        [fieldNames.firstname]: { ...FFHelpers.ffInitialFieldState.input },
        [fieldNames.lastname]:  { ...FFHelpers.ffInitialFieldState.input },
        [fieldNames.email]:     { ...FFHelpers.ffInitialFieldState.input, value: user.email },
        [fieldNames.phone]:     { ...FFHelpers.ffInitialFieldState.input },
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Add or Update ddUser

        const userResult = userProfileCompleted ? await updateDDUser() : await addDDUser();

        onFormSubmit(userResult);

        if (userResult.error) {
            console.error(`Error ${userProfileCompleted ? 'updating' : 'creating'} user: `, userResult.error);
            return;
        }

        const ddUserExists = await fetchRetryWithDelay(() => getDDUser(user.sub, true), 3000, 3);
        setUserProfileCompleted(ddUserExists);
    }

    const addDDUser = async () => {
        const accessToken = await getAccessTokenSilently();

        const ddUserObj = {
            username: formRef?.formState.fields[fieldNames.username].value,
            firstname: formRef?.formState.fields[fieldNames.firstname].value,
            lastname: formRef?.formState.fields[fieldNames.lastname].value,
            phone: formRef?.formState.fields[fieldNames.phone].value,
            email: user.email,
            userId: user.sub,
        }

        return await svc_createUser(accessToken, ddUserObj);
    }

    const updateDDUser = async () => {
        const accessToken = await getAccessTokenSilently();

        const updatedUserData = {
            firstname: formRef?.formState.fields[fieldNames.firstname].value,
            lastname: formRef?.formState.fields[fieldNames.lastname].value,
            phone: formRef?.formState.fields[fieldNames.phone].value,
        }

        return await svc_updateUser(accessToken, user.sub, updatedUserData);
    }

    useEffect(() => {
        if(formRef?.updateFieldValues && ddUser && userProfileCompleted && !appliedDDUserData) {
            formRef?.updateFieldValues({
                [fieldNames.username]: {
                    value: ddUser.username
                },
                [fieldNames.firstname]: {
                    value: ddUser.firstname
                },
                [fieldNames.lastname]: {
                    value: ddUser.lastname
                },
                [fieldNames.email]: {
                    value: user.email
                },
                [fieldNames.phone]: {
                    value: ddUser.phone
                },
            });
            setAppliedDDUserData(true);
        }
    }, [ddUser, userProfileCompleted, appliedDDUserData, formRef?.updateFieldValues])
    
    return (
        <RCFormProvider
            id="profile-form"
            initialFieldState={initialState}
            setFormRef={setFormRef}
            onSubmit={handleSubmit}
            noValidate
        >
            <FormContent
                fieldNames={fieldNames}
                isExistingUser={ddUser && userProfileCompleted}
                user={user}
            />
        </RCFormProvider>
    )
}

const FormContent = ({
    fieldNames,
    isExistingUser,
}) => {
    return (
        <>
            <FFText
                label="Username"
                fieldName={fieldNames.username}
                description={<UserNameMessage isExistingUser={isExistingUser} />}
                required={!isExistingUser}
                readOnly={isExistingUser}
                wrapperModifierClass={"formField"}
            />

            <FFText
                label="First Name"
                fieldName={fieldNames.firstname}
                required={true}
                wrapperModifierClass={"formField"}
            />

            <FFText
                label="Last Name"
                fieldName={fieldNames.lastname}
                required={true}
                wrapperModifierClass={"formField"}
            />

            <FFEmail
                label="Email"
                fieldName={fieldNames.email}
                description={<span>This is the email you entered when signing up. If you need to change your email address, please <Link to="/support">contact support</Link>.</span>}
                readOnly={true}
                wrapperModifierClass={"formField"}
            />

            <FFTelephone
                label="Phone"
                fieldName={fieldNames.phone}
                required={false}
                wrapperModifierClass={"formField"}
            />

            <div className="spc-m-t-1">
                <button type="submit" className="button button-slim button-green">
                    Submit
                </button>
            </div>
        </>
    )
}

const UserNameMessage = ({
    isExistingUser
}) => {
    return (
        <div>
            {isExistingUser
                ? <span>Username cannot be changed after creation.</span>
                : <span>Username cannot be changed after creation. Make sure you're happy with it!</span>
            }
        </div>
    )
}

export default ProfileForm;